import React, { useState, useEffect, useCallback } from "react"
import claraLogo from "../../assets/images/clara-logo.png"
import { ClaraChat } from "./ClaraChat"
import { ClaraSidebar } from "../../Components/Clara/ClaraSidebar"
import { useEndpointProvider } from "../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider"
import { useEnvironmentVariablesProvider, Deployment } from "../../Providers/EnvironmentVariablesProvider"
import { patientsEndpoint, readPatientLabels, recordingsOf, searchPatients } from "./claraPatients"
import styles from "./Clara.module.css"

// Start unselected — pick a patient from the dropdown (the dashboard resolves the
// correct data_object + reference-end + data path). Hardcoding raw DB ids is
// unreliable here because the on-disk folder, upload, patient, and data_object ids
// all differ. For the non-flat demo recording, choose "Patient_zn0jeTVtmYw7WVk7qsON"
// (that's Patient_2017_Nov_28 / data_object 16).
const DEMO = { patientId: "", dataObjectId: "", referenceEndMs: "" }

export default function Clara() {
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const { deployment } = useEnvironmentVariablesProvider()

	// The patients we have actually seen: the first page, plus anything a search or a
	// pick has surfaced since. NOT "the patients that exist" — that list lives on the
	// server, and treating a prefetched slice of it as the whole ward is what made an
	// admitted patient unfindable at UTSW. Anything that needs a patient we have not
	// seen goes and asks (see findPatients).
	const [patients, setPatients] = useState([])
	const [selectedPatient, setSelectedPatient] = useState(null)
	// The archive Clara is reading, with its own span. Held so the chat can name it and
	// state the period, which is also where "the past 4 hours" and "yesterday" are
	// anchored from.
	const [selectedRecording, setSelectedRecording] = useState(null)
	const [patientId, setPatientId] = useState(DEMO.patientId)
	const [dataObjectId, setDataObjectId] = useState(DEMO.dataObjectId)
	const [referenceEndMs, setReferenceEndMs] = useState(DEMO.referenceEndMs)
	const [started, setStarted] = useState(false)

	const endpoint = patientsEndpoint(LINKS, deployment === Deployment.CLINICAL)

	// Remember every patient we are handed, so a chip click or a reopened chat can
	// resolve one without going back to the server for a row we already have.
	const remember = useCallback(found => {
		setPatients(known => {
			const byKey = new Map(known.map(patient => [String(patient.patient_key), patient]))
			found.forEach(patient => byKey.set(String(patient.patient_key), patient))
			return [...byKey.values()]
		})
		return found
	}, [])

	// A first page, only so "watch patient" with no name can offer something to click.
	useEffect(() => {
		searchPatients(endpointProvider, endpoint, { limit: 25 }).then(remember).catch(() => {})
	}, [endpointProvider, endpoint, remember])

	// Find patients by name or id. Goes to the SERVER, because the browser only ever
	// holds the patients it has already seen: "watch patient RR9331CO" used to search a
	// prefetched page and answer "I couldn't find a patient matching RR9331CO" for a
	// patient who was admitted at the time.
	const findPatients = useCallback(
		term => searchPatients(endpointProvider, endpoint, { term }).then(remember).catch(() => []),
		[endpointProvider, endpoint, remember]
	)

	// Returns the resolved ids so a caller (e.g. a one-sentence "heart rate for
	// Patient_X" question) can run a data query immediately, without waiting for the
	// reference-end-ms state to propagate on the next render.
	const selectPatient = useCallback(async (keyOrPatient) => {
		// The picker and the chat chips hand over the whole row they were rendered from;
		// a reopened conversation only knows the key. Take either.
		let patient =
			keyOrPatient && typeof keyOrPatient === "object"
				? keyOrPatient
				: patients.find(p => String(p.patient_key) === String(keyOrPatient))
		if (!patient && keyOrPatient != null) {
			/* A reopened chat knows only the KEY, and the session cache may not hold
			   that patient: fresh page with the initial list fetch still in flight, or
			   a patient beyond the first page. This is exactly the reported bug, a chat
			   named for its patient reopening with no patient selected. Resolve from
			   the server: search by the chat's remembered label first (it is the name
			   the rail shows), then a wider unfiltered page, and in both cases match
			   the KEY exactly, never the label, so a shared name cannot select the
			   wrong patient. */
			try {
				const byKey = rows => (rows || []).find(p => String(p.patient_key) === String(keyOrPatient))
				const label = readPatientLabels()[String(keyOrPatient)] || ""
				if (label) {
					patient = byKey(await searchPatients(endpointProvider, endpoint, { term: label }))
				}
				if (!patient) {
					patient = byKey(await searchPatients(endpointProvider, endpoint, { limit: 200 }))
				}
			} catch (error) {
				// Fall through: the transcript stays readable, the picker still works.
			}
		}
		if (!patient) {
			return { patientId: "", dataObjectId: "", referenceEndMs: "" }
		}
		setSelectedPatient(patient)
		remember([patient])
		const recordings = recordingsOf(patient)
		const pid = String(patient.patient_key)
		setPatientId(pid)
		setReferenceEndMs("")
		setSelectedRecording(null)
		if (!recordings.length) {
			setDataObjectId("")
			return { patientId: pid, dataObjectId: "", referenceEndMs: "", noRecording: true }
		}
		try {
			const timestamps = await endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT_FILE_START_END_TIMESTAMPS, {
				patient_ids: [patient.patient_key],
			})
			// A patient can have several archives. Watch the latest one whose files are
			// actually THERE: the recording whose monitoring window ends most recently,
			// among those the server says it can find. The backend's list order is not
			// chronological, so taking the first would pick an arbitrary (often stale) file.
			//
			// The existence half is the fix. Picking the latest regardless is how Clara
			// came to be reading an archive that is on no disk, listing seven signals out
			// of the database catalogue and then answering "I don't have that data" to
			// every question about them. Data Review greys the same file out and refuses
			// to open it; Clara selected it and carried on talking.
			const readable = recordings.filter(rec => rec.exists)
			let latest = null
			let latestEnd = -Infinity
			for (const rec of readable) {
				const end = Number(timestamps?.[rec.name]?.monitoring_end_time)
				if (Number.isFinite(end) && end > latestEnd) {
					latestEnd = end
					latest = rec
				}
			}
			const chosen = latest ?? readable[0]
			if (!chosen) {
				// Every archive this patient has is missing from the server. Say so once,
				// here, instead of letting every later question fail on its own.
				setDataObjectId("")
				setSelectedRecording(null)
				return { patientId: pid, dataObjectId: "", referenceEndMs: "", noReadableRecording: true }
			}
			const recording = {
				...chosen,
				start: Number(timestamps?.[chosen.name]?.monitoring_start_time) || null,
				end: Number.isFinite(latestEnd) ? latestEnd : null,
			}
			setSelectedRecording(recording)
			const did = String(chosen.id)
			const endMs = latest ? String(latestEnd) : ""
			setDataObjectId(did)
			if (endMs) {
				setReferenceEndMs(endMs)
			}
			return { patientId: pid, dataObjectId: did, referenceEndMs: endMs, recording }
		} catch (error) {
			// Timestamps unavailable: fall back to a readable archive, blank reference end.
			// Still never a missing one, since that fails every question that follows.
			const fallback = recordings.find(rec => rec.exists)
			if (!fallback) {
				setDataObjectId("")
				return { patientId: pid, dataObjectId: "", referenceEndMs: "", noReadableRecording: true }
			}
			setSelectedRecording(fallback)
			setDataObjectId(String(fallback.id))
			return { patientId: pid, dataObjectId: String(fallback.id), referenceEndMs: "", recording: fallback }
		}
	}, [patients, remember, endpointProvider, endpoint, LINKS])

	const patientReady = Boolean(patientId && dataObjectId && referenceEndMs)

	// The rail is a SIBLING of the conversation, owned here rather than inside ClaraChat.
	// Rendered inside, the chat's own scroll container and stacking context clipped its
	// header; out here nothing the conversation does can reach it.
	// Closed by default. The conversation is the page; the history is somewhere you go
	// when you want it, not a permanent column competing with the answer.
	const [railCollapsed, setRailCollapsed] = useState(true)
	const [rail, setRail] = useState({ conversations: [], activeId: null, sources: [] })

	return (
		<div className={styles.page}>
			{/* The brand mark used to fly from the welcome screen to the top left and dock
			    there with a wordmark. The rail carries the name now, so the corner is left
			    to the conversation and the patient. The logo still opens the page, inside
			    the welcome overlay, and leaves with it. */}
			<div className={`${styles.welcome} ${started ? styles.welcomeHidden : ""}`}>
				<div className={styles.welcomeGlow} />
				<img src={claraLogo} alt="" aria-hidden="true" className={styles.welcomeLogo} />
				<div className={styles.askLine}>
					Hey, I’m <span className={styles.wordmark}>Clara</span>
					<br />
					how can I help you?
				</div>
				<div className={styles.welcomeHint}>
					Pick a patient below to begin, or just say “watch patient …” and I’ll find them.
				</div>
			</div>

			<div className={styles.body}>
			<ClaraChat
				onRailData={setRail}
				patients={patients}
				findPatients={findPatients}
				selectedPatient={selectedPatient}
				selectedRecording={selectedRecording}
				patientId={patientId}
				dataObjectId={dataObjectId}
				referenceEndMs={referenceEndMs}
				patientReady={patientReady}
				onSelectPatient={selectPatient}
				onStart={() => setStarted(true)}
				onNewChat={() => {
					// Clean slate: back to the welcome screen with no patient selected.
					setStarted(false)
					setSelectedPatient(null)
					setPatientId(DEMO.patientId)
					setDataObjectId(DEMO.dataObjectId)
					setReferenceEndMs(DEMO.referenceEndMs)
				}}
			/>
			<ClaraSidebar
				conversations={rail.conversations}
				activeId={rail.activeId}
				sources={rail.sources}
				onNewChat={rail.onNewChat}
				onOpen={rail.onOpen}
				collapsed={railCollapsed}
				onToggle={() => setRailCollapsed(value => !value)}
			/>
			</div>
		</div>
	)
}
